import { Injectable, Injector } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { ClipboardService } from "ngx-clipboard";
import { NotificationService } from "app/shared/components/notification";
import { ResponseViewModel } from "app/shared/models/core";
import { PermissionNavigationResponse, PermissionResponse } from "../edduca-imob";
import { FuseNavigationItem } from "@fuse/components/navigation";
import { TableColumn } from "app/shared/interfaces";
import { AuthService } from "app/core/auth/auth.service";
import { UsuarioService } from "../edduca-imob/usuario/usuario.service";
import { Observable, take, catchError, of } from "rxjs";
import { PermissionCheckService } from "./permission-check.service";

@Injectable({
  providedIn: 'root'
})
export class GlobalService {
  private _id: number = 0;
  private _authService: AuthService | null = null;
  private _permissionCheckService: PermissionCheckService | null = null;

  breadcrums: string[] = [];
  currentBread: string = '';

  constructor(
    private injector: Injector,
    public notification: NotificationService,
    private _router: Router,
    private _clipboardService: ClipboardService,
    private _usuarioService: UsuarioService,
  ) {}

  private get authService(): AuthService {
    if (!this._authService) {
      this._authService = this.injector.get(AuthService);
    }
    return this._authService;
  }

  private get permissionCheckService(): PermissionCheckService {
    if (!this._permissionCheckService) {
      this._permissionCheckService = this.injector.get(PermissionCheckService);
    }
    return this._permissionCheckService;
  }

  getIdUserLogged(): number {
    return this.authService.getTokenInfo()?.userId ?? 0;
  }

  signOut(): void {
    this.clearPermissions();
    this.clearProfilePicture();
    this._router.navigateByUrl('/login');
  }

  /**
   * Verifica se o usuário tem permissão para acessar uma interface/rota.
   * Utiliza o PermissionCheckService para validar baseado no perfil do usuário.
   * @param interfaceUrl URL da interface a ser verificada
   * @returns true se o usuário tem permissão, false caso contrário
   */
  verifyInterfacePermission(interfaceUrl: string): boolean {
    return this.permissionCheckService.hasRoutePermission(interfaceUrl);
  }

  temPermicaoFuncionalidade(idFuncionalidade: number): Observable<boolean> {
    return this._usuarioService
      .temPermicaoFuncionalidade(this.getIdUserLogged(), idFuncionalidade)
      .pipe(
        take(1),
        catchError(() => of(false))
      );
  }

  handledErrorMessage(error: ResponseViewModel): void {
    if (error?.didError)
      this.notification.error(error.message);
  }

  unhandledErrorMessage(error: ResponseViewModel): void {
    this.notification.error(error.errorMessage);
  }

  errorMessage(error: ResponseViewModel) {
    this.notification.error(error.errorMessage);
  }

  patchFormValueToObject(form: FormGroup, model: { [key: string]: any }, fieldsExcep: Array<string> = []) {
    for (const [key, control] of Object.entries(form.controls)) {
      if (model.hasOwnProperty(key) && !fieldsExcep.includes(key)) {
        model[key] = control.value;
      }
    }
    return model;
  }

  setPermissions(data: PermissionNavigationResponse) {
    this.clearPermissions();
    localStorage.setItem('permissions', JSON.stringify(data.permissions));
    localStorage.setItem('navigations', JSON.stringify(data.navigations));
  }

  clearPermissions() {
    localStorage.removeItem('permissions');
    localStorage.removeItem('navigations');
  }

  setProfilePicture(userPicture: string) {
    this.clearProfilePicture();
    localStorage.setItem('userPicture', userPicture);
  }

  clearProfilePicture() {
    localStorage.removeItem('userPicture');
  }

  getNavigation(): Array<FuseNavigationItem> {
    return JSON.parse(localStorage.getItem('navigations') ?? '[]');
  }

  findLabelByRoute(targetRoute: string): string {
    const search = (menu: FuseNavigationItem[], target: string): string | null => {
      for (const item of menu) {
        if (!item.children && item.link === target) {
          return item.title;
        } else if (item.children) {
          const result = search(item.children, target);
          if (result) return result;
        }
      }
      return null;
    };
    return search(this.getNavigation(), targetRoute) ?? '';
  }

  completeAnotherFormMessage(header?: string) {
    this.notification.success('Verifique se todas as informações do cadastro foram preenchidas!', header);
  }

  registerSaveSuccessMessage(header?: string) {
    this.notification.success('Registro salvo com sucesso!', header);
  }

  registerInsertSuccessMessage(header?: string) {
    this.notification.success('Registro cadastrado com sucesso!', header);
  }

  registerUpdateSuccessMessage(header?: string) {
    this.notification.success('Registro atualizado com sucesso!', header);
  }

  registerDeleteSuccessMessage(header?: string) {
    this.notification.success('Registro apagado com sucesso!', header);
  }

  resetSuccessMessage(header?: string) {
    this.notification.success('Registro resetado com sucesso!', header);
  }

  inactiveSuccessMessage(header?: string) {
    this.notification.success('Registro inativado com sucesso!', header);
  }

  toggleColumnVisibility(column: any, event: any) {
    event.stopPropagation();
    event.stopImmediatePropagation();
    column.visible = !column.visible;
  }

  generateFile(result: any) {
    if (result.base64) {
      const byteArray = new Uint8Array([...atob(result.base64)].map(char => char.charCodeAt(0)));
      const blob = new Blob([byteArray], { type: result.contentType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = result.name;
      link.click();

      window.URL.revokeObjectURL(url);
    } else {
      this.downloadArquivo(result);
    }
  }

  downloadArquivo(data: any) {
    const url = data.url;
    const link = document.createElement('a');
    link.href = url;
    link.download = url;
    link.click();
    window.open(url, '_blank');
    window.URL.revokeObjectURL(url);
  }

  setId(id: any) {
    this._id = id;
  }

  getId(): any {
    return this._id;
  }

  trackByProperty<T>(_index: number, column: TableColumn<T>) {
    return column.property;
  }

  transformaForm(form: any, control: any, clean?: boolean) {
    if (form?.controls?.[control]) {
      if (clean) {
        form.controls[control].clearValidators();
        form.controls[control].reset();
      } else {
        form.controls[control].setValidators([Validators.required]);
      }
    }
  }

  transformaFormValidators(form: any, control: any, validationName?: any) {
    if (form?.controls?.[control]) {
      form.controls[control].clearValidators();
      form.controls[control].setValidators([Validators.required, ...(validationName ? [validationName] : [])]);
    }
  }

  resetControlForm(form: any, control: any, clean?: boolean) {
    if (form?.controls?.[control]) {
      if (clean) form.controls[control].setValue(null);
      form.controls[control].markAsPristine();
      form.controls[control].markAsUntouched();
      form.controls[control].updateValueAndValidity();
    }
  }

  resetControl(form: any, control: any, clean?: boolean, required?: boolean, mostrarErros?: boolean) {
    this.resetControlForm(form, control, clean);
    form.controls[control].clearValidators();
    if (required) form.controls[control].setValidators([Validators.required]);
    if (mostrarErros) form.controls[control].markAsTouched();
  }

  mostrarErrosForm(form: any, imprimir = true) {
    const controls = form.controls;
    Object.keys(controls).forEach(key => {
      if (controls[key].errors && imprimir) {
        console.log(`control: ${key}, error: ${this.print(controls[key].errors)}, value: ${controls[key].value}`);
      }
      controls[key].markAsTouched();
    });
  }

  mostrarValoresForm(form: any, imprimir = true) {
    const controls = form.controls;
    Object.keys(controls).forEach(key => {
      if (imprimir) {
        console.log(`control: ${key}, value: ${controls[key].value}`);
      }
      controls[key].markAsTouched();
    });
  }

  private print(errors: any) {
    return errors ? Object.keys(errors).join(';') : null;
  }

  fileToBase64(file: File): string {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => reader?.result?.toString();
    return "";
  }

  base64ToArrayBuffer(base64: string) {
    return Array.from(new Uint8Array(atob(base64).length));
  }

  getDateText(): string {
    const now = new Date();
    return `${now.getFullYear()}${now.getMonth()}${now.getDate()}${now.getHours()}${now.getMinutes()}${now.getSeconds()}`;
  }

  copyClipboard(text: string) {
    this._clipboardService.copyFromContent(text);
  }
}
